import * as Scrivito from "scrivito";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import cx from "classnames";
import { useFormikContext } from "formik";
import { isEqual } from "lodash-es";
import { useTranslate } from "hooks";
import { Button, XCircleLoader } from "components";
import { WizardTitleButtons } from "objs/_ConfiguratorObjs/Wizard/SubComponents/WizardTitleButtons";
import { WizardEditableTitle } from "objs/_ConfiguratorObjs/Wizard/SubComponents/WizardEditableTitle";
import {
  useConstraints,
  useIsFetching,
  useIsLastAvailableStep,
  useIsMultipleQuestionnaireListStep,
  useWizardActions,
  useWizardStep,
} from "objs/_ConfiguratorObjs/Wizard/stores/wizardStore";
import {
  getAnswersFromValues,
  getFormInitialValues,
} from "./questionnaireHelpers";
import { WizardSingleQuestionnaireControls } from "./WizardSingleQuestionnaireControls";
import { WizardForm } from "./WizardForm";
import { WizardQuestionnaireTitleIcons } from "./WizardQuestionnaireTitleIcons/WizardQuestionnaireTitleIcons";
import s from "./WizardQuestionnaire.module.scss";

export const WizardQuestionnaire = Scrivito.connect(
  ({ currentQuestionnaire }) => {
    const translate = useTranslate();
    const { values, dirty, resetForm, setValues } = useFormikContext();
    const [isEditMode, setEditMode] = useState(false);
    const step = useWizardStep();
    const isFetching = useIsFetching();

    const isLastAvailableStep = useIsLastAvailableStep();
    const {
      changeWizardStep,
      setIsFormDirty,
      goToNextStep,
      updateWizardStepQuestionnaire,
    } = useWizardActions();
    const constraints = useConstraints();
    const isMultipleQuestionnaireListStep =
      useIsMultipleQuestionnaireListStep();
    const { questions, title, description, info, position, id } =
      currentQuestionnaire;

    const handleToggleEditMode = useCallback(() => {
      setEditMode((val) => !val);
    }, []);

    const handleSubmit = useCallback(() => {
      if (!isLastAvailableStep && !dirty) {
        goToNextStep();
      } else {
        changeWizardStep({
          answersLists: [
            {
              identifier: id,
              title,
              questions: getAnswersFromValues(questions, values),
            },
          ],
          onCancelAction: () => resetForm(),
        });
      }
    }, [
      isLastAvailableStep,
      dirty,
      resetForm,
      changeWizardStep,
      id,
      title,
      questions,
      values,
      goToNextStep,
    ]);

    useEffect(() => {
      if (isMultipleQuestionnaireListStep) {
        setIsFormDirty(dirty);
      }
    }, [setIsFormDirty, dirty, isMultipleQuestionnaireListStep]);

    const handleConfirmSingleQuestionnaire = useCallback(() => {
      updateWizardStepQuestionnaire({
        questionnaire: [
          {
            title,
            questions: getAnswersFromValues(questions, values),
          },
        ],
        id,
      }).then(() => {
        resetForm({ values });
      });
    }, [
      updateWizardStepQuestionnaire,
      id,
      title,
      questions,
      values,
      resetForm,
    ]);

    const isFieldsConstraintViolated = useMemo(() => {
      const fieldsConstraint = constraints.find((constraint) =>
        constraint.scope.includes("fields.fieldValue"),
      );

      if (!fieldsConstraint) {
        return false;
      }

      const { maxValue, minValue, reference } = fieldsConstraint;
      if (reference === "sum") {
        const sum = Object.values(values).reduce((accum, value) => {
          if (typeof value !== "object") {
            return accum + +value;
          }
          return accum;
        }, 0);

        return sum < minValue || sum > maxValue;
      }
      return false;
    }, [constraints, values]);

    const memoizedInitialValues = useMemo(
      () => getFormInitialValues(questions, true),
      [questions],
    );
    const handleReset = () => {
      setValues({
        ...values,
        ...memoizedInitialValues,
      });
    };

    const zoneNumber = useMemo(
      () => position.toString().padStart(3, "0"),
      [position],
    );

    const isResetEnabled = useMemo(() => {
      const {
        fieldsRelation: _fieldsRelation,
        resetFieldValue: _resetFieldValue,
        ...filteredValues
      } = values;
      const { resetFieldValue: _ignored, ...filteredInitialValues } =
        memoizedInitialValues;

      return isEqual(filteredValues, filteredInitialValues);
    }, [memoizedInitialValues, values]);

    if (isFetching) {
      return <XCircleLoader />;
    }

    return (
      <>
        <div
          className="position-sticky top-0 pt-4 pb-2 bg-gray-100 z-1"
          data-testid="wizard-questionnaire"
        >
          <div className="d-flex justify-content-between mb-2">
            <div>
              <div className="d-flex gap-2 align-items-center position-relative">
                {isEditMode ? (
                  <WizardEditableTitle
                    title={title}
                    zoneId={id}
                    inputClassNames="fw-bold text-blue-800 ps-2 fs-lg"
                    handleToggleEditMode={handleToggleEditMode}
                  />
                ) : (
                  <h3
                    onClick={step > 1 ? handleToggleEditMode : undefined}
                    className={cx("text-uppercase mb-0", {
                      "cursor-pointer": step > 1,
                    })}
                  >
                    {title}
                  </h3>
                )}
                {isMultipleQuestionnaireListStep && (
                  <>
                    <div
                      className={cx(
                        "d-flex justify-content-center align-items-center",
                        s.flexGapPosition,
                      )}
                    >
                      {zoneNumber}
                    </div>

                    {!isEditMode && (
                      <Button
                        onClick={handleToggleEditMode}
                        data-testid="editIcon"
                        square={true}
                        color="tertiary"
                        iconPrefix="trox"
                        icon="pencil-light"
                        iconClassName="icon-16"
                        classNames="p-0 ms-1 border-0 cursor-pointer pe-2"
                      />
                    )}
                  </>
                )}
              </div>
              {description && <h4 className="mt-1 mb-0">{description}</h4>}
            </div>
            {!isMultipleQuestionnaireListStep ? (
              <WizardTitleButtons />
            ) : (
              <WizardQuestionnaireTitleIcons
                isFieldsConstraintViolated={isFieldsConstraintViolated}
                isContinueStepFetching={false}
              />
            )}
          </div>
          {info && <div className="text-gray-700 font16 mt-2">{info}</div>}
          <WizardSingleQuestionnaireControls
            handleClickContinue={handleSubmit}
            handleConfirmSingleQuestionnaire={handleConfirmSingleQuestionnaire}
            isFieldsConstraintViolated={isFieldsConstraintViolated}
            handleResetForm={handleReset}
            isResetEnabled={isResetEnabled}
          />
          {isMultipleQuestionnaireListStep && (
            <div className="text-gray-700 font16 mb-4">
              {translate("WIZARD_FILLING_ORDER_INFO")}
            </div>
          )}
        </div>
        {currentQuestionnaire && <WizardForm questions={questions} />}
      </>
    );
  },
);
